/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Impersonation Banner Component
 * Shows a warning banner when an admin is impersonating a user
 */

import { useState } from 'react'
import { useAuthStore } from '../stores/authStore'
import { AlertTriangle, X, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

const ImpersonationBanner = () => {
  const { isImpersonating, user, stopImpersonation } = useAuthStore()
  const [stopping, setStopping] = useState(false)

  if (!isImpersonating) return null

  const handleStopImpersonation = async () => {
    setStopping(true)
    try {
      const result = await stopImpersonation()

      if (result.success) {
        toast.success('Impersonation ended')

        if (result.requiresLogin) {
          window.location.href = '/login'
        } else {
          window.location.href = '/admin/users'
        }
      } else {
        toast.error(result.error || 'Failed to stop impersonation')
      }
    } catch (error) {
      toast.error('Failed to stop impersonation')
    } finally {
      setStopping(false)
    }
  }

  return (
    <div className="bg-yellow-500 text-yellow-900 px-4 py-2 flex items-center justify-center gap-3 text-sm font-medium fixed top-0 left-0 right-0 z-50 h-10">
      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
      <span>
        You are impersonating <strong>{user?.email || 'a user'}</strong>
      </span>
      <button
        onClick={handleStopImpersonation}
        disabled={stopping}
        className="ml-2 px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-xs font-semibold disabled:opacity-50 flex items-center gap-1"
      >
        {stopping ? (
          <>
            <Loader2 className="w-3 h-3 animate-spin" />
            Stopping...
          </>
        ) : (
          'Stop Impersonation'
        )}
      </button>
    </div>
  )
}

export default ImpersonationBanner
